import BASE_CHECK_TYPE from '../../constants/regular.constant';
import type {
  ArchiveFormFieldRule,
  ArchiveFormProgressResult,
  ArchiveFormSectionRule,
} from './archive-form.types';

/**
 * 判断字段值是否视为空（未填写）。
 * @param value - 表单字段当前值
 */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
}

/**
 * 校验单个可见字段是否存在阻断保存的错误。
 * @param field - 字段规则
 * @param value - 字段当前值
 */
function hasFieldError(field: ArchiveFormFieldRule, value: unknown): boolean {
  if (field.hidden) {
    return false;
  }

  const empty = isEmptyValue(value);

  if (field.required && empty) {
    return true;
  }

  if (!empty && field.regularType) {
    const rule = BASE_CHECK_TYPE[field.regularType];
    if (rule && !rule.regular.test(String(value))) {
      return true;
    }
  }

  return false;
}

/**
 * 判断可见必填字段是否已填写且格式有效。
 * @param field - 字段规则
 * @param value - 字段当前值
 */
function isRequiredFieldCompleted(field: ArchiveFormFieldRule, value: unknown): boolean {
  if (field.hidden || !field.required) {
    return false;
  }

  if (isEmptyValue(value)) {
    return false;
  }

  if (field.regularType) {
    const rule = BASE_CHECK_TYPE[field.regularType];
    if (rule && !rule.regular.test(String(value))) {
      return false;
    }
  }

  return true;
}

/**
 * 计算整张建档表单的实时完成状态。
 * @param sections - 分区与字段规则
 * @param data - 当前表单数据
 * @param pendingConfirmCount - OCR 待确认字段数
 */
export function calculateArchiveFormProgress(
  sections: readonly ArchiveFormSectionRule[],
  data: Record<string, unknown>,
  pendingConfirmCount = 0,
): ArchiveFormProgressResult {
  // 可见必填字段总数
  let requiredTotal = 0;
  // 已填写且格式有效的可见必填字段数
  let requiredCompleted = 0;
  // 阻断保存的错误字段总数
  let errorCount = 0;
  // 各分区剩余必填数
  const sectionResults: Array<{ key: string; pendingRequired: number }> = [];

  for (const section of sections) {
    // 当前分区尚未完成的可见必填字段数
    let pendingRequired = 0;

    for (const field of section.fields) {
      if (field.hidden) {
        continue;
      }

      const value = data[field.key];

      if (field.required) {
        requiredTotal += 1;
        if (isRequiredFieldCompleted(field, value)) {
          requiredCompleted += 1;
        } else {
          pendingRequired += 1;
        }
      }

      if (hasFieldError(field, value)) {
        errorCount += 1;
      }
    }

    sectionResults.push({ key: section.key, pendingRequired });
  }

  // 无必填字段时完成度为 100
  const percentage = requiredTotal === 0
    ? 100
    : Math.round((requiredCompleted / requiredTotal) * 100);

  const canSave = errorCount === 0 && pendingConfirmCount === 0;

  return {
    requiredTotal,
    requiredCompleted,
    percentage,
    errorCount,
    pendingConfirmCount,
    canSave,
    sections: sectionResults,
  };
}
